'use client';

import { useEffect, useRef } from 'react';
import { useCallOperatorControls, useCallOperatorState } from '../lib/callixSdk';
import { API_BASE_URL, getAuthHeaders } from '../lib/widgetApi';
import {
  ackClickToCall,
  createBackgroundTicker,
  pollClickToCall,
  reportClickToCallOffline,
  type AckOptions,
  type BackgroundTicker,
  type PollResult,
} from '../lib/clickToCallClient';
import {
  canDispatchTo,
  CLICK_TO_CALL_EVENT,
  presenceFromOperatorState,
  type ClickToCallTaskPublic,
} from '../lib/clickToCallTypes';

export interface ClickToCallAgentProps {
  /** Desliga o polling (padrão: ligado). */
  enabled?: boolean;
  /** Intervalo base entre consultas; sem ele vale o sugerido pelo servidor (4 s). Limitado à metade do TTL da presença. */
  pollIntervalMs?: number;
  /** Token da sessão do SDK. Sem ele o agente lê o token salvo no localStorage. */
  sessionToken?: string | null;
  /** Chamado ao receber uma tarefa, antes de discar. */
  onTask?: (task: ClickToCallTaskPublic) => void;
}

export const DEFAULT_CLICK_TO_CALL_INTERVAL_MS = 4000;
/** Em chamada/qualificando só a presença é atualizada: consulta mais espaçada. */
const BUSY_INTERVAL_FACTOR = 3;
/** Depois de disparar (ou devolver) uma ligação, espera o SDK mudar de estado antes de pedir outra tarefa. */
const DISPATCH_COOLDOWN_MS = 6000;
/** Após makeManualCall, quanto esperar o SDK entrar na chamada manual antes de desistir. */
const CALL_START_TIMEOUT_MS = 4000;
const CALL_START_CHECK_MS = 100;
/** Operador em pausa: quanto esperar o SDK voltar a disponível (becomeAvailable) antes de devolver a tarefa à fila. */
const LEAVE_BREAK_TIMEOUT_MS = 8000;
/** Falhas seguidas (rede, 401, 5xx): intervalo dobra a cada falha até este teto. */
const MAX_BACKOFF_MS = 60_000;
/** Servidor sem fila/chave configurada: consulta esparsa (não desliga, para pegar a configuração quando chegar). */
const NOT_CONFIGURED_INTERVAL_MS = 5 * 60_000;
const NOT_CONFIGURED_CODES = new Set(['STORE_NOT_CONFIGURED', 'SERVER_NOT_CONFIGURED']);
/** A confirmação (PATCH) é repetida em falha de rede/5xx: o servidor dá 45 s para ela chegar. */
const ACK_ATTEMPTS = 3;
const ACK_RETRY_DELAY_MS = 1500;

type PollMode = 'normal' | 'backoff' | 'notConfigured';
type CallStartOutcome = 'started' | 'manual_failed' | 'interrupted' | 'not_started' | 'disposed';

const CALL_STARTED_STATES = new Set(['manualCallSetup', 'manualCallRinging', 'callInProgress', 'afterCall']);

function buildHeaders(sessionToken?: string | null): Record<string, string> {
  if (sessionToken) {
    return { 'Content-Type': 'application/json', 'x-session-token': sessionToken, Authorization: `Bearer ${sessionToken}` };
  }
  return getAuthHeaders();
}

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Agente invisível do click-to-call. Renderizado DENTRO do CallixClientProvider:
 * consulta o backend, e quando chega uma tarefa para o usuário logado (disponível
 * ou em pausa) dispara `makeManualCall` - exatamente o que o discador faz ao tocar
 * em "ligar" - e avisa os discadores pelo evento `callix:click-to-call` para que
 * mostrem o número e sigam para a tela de chamada. Em pausa, primeiro sai da pausa
 * (becomeAvailable) e espera o SDK ficar disponível; se não ficar, devolve à fila.
 */
export function ClickToCallAgent({ enabled = true, pollIntervalMs, sessionToken, onTask }: ClickToCallAgentProps) {
  const operatorState = useCallOperatorState();
  const { makeManualCall, becomeAvailable } = useCallOperatorControls();
  const stateRef = useRef<string | undefined>(operatorState?.state);
  stateRef.current = operatorState?.state;
  const makeManualCallRef = useRef(makeManualCall);
  makeManualCallRef.current = makeManualCall;
  const becomeAvailableRef = useRef(becomeAvailable);
  becomeAvailableRef.current = becomeAvailable;
  const onTaskRef = useRef(onTask);
  onTaskRef.current = onTask;
  const tickerRef = useRef<BackgroundTicker | null>(null);
  const applyIntervalRef = useRef<(() => void) | null>(null);

  // Intervalo acompanha o estado: disponível/pausa = base; ocupado = base x 3 (limitado pelo TTL da presença).
  useEffect(() => {
    applyIntervalRef.current?.();
  }, [operatorState?.state]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    let disposed = false;
    let inFlight = false;
    let cooldownUntil = 0;
    let failureStreak = 0;
    let mode: PollMode = 'normal';
    let notConfiguredWarned = false;
    let presenceTtlMs = 45_000;
    let baseInterval = Math.max(1000, pollIntervalMs ?? DEFAULT_CLICK_TO_CALL_INTERVAL_MS);
    // Cabeçalhos fixados na montagem: no logout o widget apaga o token do localStorage
    // antes de desmontar o agente, e o aviso de "offline" precisa sair autenticado.
    const config = { apiBaseUrl: API_BASE_URL, headers: buildHeaders(sessionToken) };

    const computeInterval = () => {
      if (mode === 'notConfigured') {
        return NOT_CONFIGURED_INTERVAL_MS;
      }
      // Nunca mais que metade do TTL da presença, senão o usuário "some" entre consultas
      const ceiling = Math.max(1000, Math.floor(presenceTtlMs / 2));
      const base = Math.min(baseInterval, ceiling);
      if (mode === 'backoff') {
        return Math.min(base * 2 ** failureStreak, MAX_BACKOFF_MS);
      }
      const busy = Math.min(base * BUSY_INTERVAL_FACTOR, ceiling);
      return canDispatchTo(presenceFromOperatorState(stateRef.current)) ? base : busy;
    };
    const applyInterval = () => tickerRef.current?.setInterval(computeInterval());
    applyIntervalRef.current = applyInterval;

    /** Confirma com repetição em falha de rede/5xx; nunca lança. */
    const ack = async (options: Omit<AckOptions, 'apiBaseUrl' | 'headers'>): Promise<boolean> => {
      for (let attempt = 0; attempt < ACK_ATTEMPTS; attempt++) {
        try {
          const result = await ackClickToCall({ ...config, ...options });
          if (result.ok) {
            return true;
          }
          const retryable = result.status >= 500 || result.status === 408 || result.status === 429;
          if (!retryable) {
            console.warn('[ClickToCall] confirmação recusada pelo servidor:', result.status, result.code ?? '', '(tarefa', options.taskId, ')');
            return false;
          }
        } catch (error) {
          console.warn('[ClickToCall] confirmação falhou (tentativa', attempt + 1, '):', error instanceof Error ? error.message : error);
        }
        if (disposed || attempt === ACK_ATTEMPTS - 1) {
          break;
        }
        await delay(ACK_RETRY_DELAY_MS * (attempt + 1));
      }
      console.error('[ClickToCall] não foi possível confirmar a tarefa', options.taskId, '; o servidor a marcará como não confirmada');
      return false;
    };

    /** Espera o estado do operador satisfazer `predicate`, dentro do prazo. */
    const waitForOperatorState = (predicate: (state: string | undefined) => boolean, timeoutMs: number) =>
      new Promise<boolean>((resolve) => {
        const deadline = Date.now() + timeoutMs;
        const check = () => {
          if (disposed) return resolve(false);
          if (predicate(stateRef.current)) return resolve(true);
          if (Date.now() >= deadline) return resolve(false);
          setTimeout(check, CALL_START_CHECK_MS);
        };
        check();
      });

    /** Espera o SDK entrar na chamada manual (prova de que discou). */
    const waitForCallStart = () =>
      new Promise<CallStartOutcome>((resolve) => {
        const deadline = Date.now() + CALL_START_TIMEOUT_MS;
        const check = () => {
          if (disposed) return resolve('disposed');
          const state = stateRef.current;
          if (state && CALL_STARTED_STATES.has(state)) return resolve('started');
          if (state === 'manualCallFailed') return resolve('manual_failed');
          // Chamada de campanha chegou antes, ou o SDK caiu: a manual não aconteceu
          if (state === 'callRinging' || presenceFromOperatorState(state) === 'offline') return resolve('interrupted');
          if (Date.now() >= deadline) return resolve('not_started');
          setTimeout(check, CALL_START_CHECK_MS);
        };
        check();
      });

    const dispatch = async (task: ClickToCallTaskPublic) => {
      const state = stateRef.current;
      // Nos dois casos abaixo esta aba não pede outra tarefa por alguns segundos
      cooldownUntil = Date.now() + DISPATCH_COOLDOWN_MS;
      if (!canDispatchTo(presenceFromOperatorState(state))) {
        console.warn('[ClickToCall] tarefa', task.id, 'recebida com operador ocupado (', state, '): devolvendo à fila');
        await ack({ taskId: task.id, status: 'failed', error: `Operador indisponível (${state ?? 'desconhecido'})`, retry: true });
        return;
      }
      console.log('[ClickToCall] 🖱️ ligação pedida pela API:', task.phoneDisplay, '(tarefa', task.id, ')');
      try {
        window.dispatchEvent(new CustomEvent<ClickToCallTaskPublic>(CLICK_TO_CALL_EVENT, { detail: task }));
      } catch (error) {
        console.warn('[ClickToCall] falha ao notificar o discador:', error);
      }
      try {
        onTaskRef.current?.(task);
      } catch (error) {
        console.warn('[ClickToCall] onTask lançou erro:', error);
      }
      if (presenceFromOperatorState(stateRef.current) === 'on_break') {
        // em pausa: sai da pausa e só disca quando o SDK estiver disponível (makeManualCall em pausa é ignorado)
        console.log('[ClickToCall] operador em pausa: saindo da pausa para ligar');
        try {
          becomeAvailableRef.current();
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error);
          console.error('[ClickToCall] becomeAvailable falhou:', message);
          await ack({ taskId: task.id, status: 'failed', error: `Não deu para sair da pausa: ${message}`, retry: true });
          return;
        }
        const available = await waitForOperatorState((state) => state === 'idle', LEAVE_BREAK_TIMEOUT_MS);
        if (!available) {
          console.warn('[ClickToCall] o operador não saiu da pausa a tempo (estado', stateRef.current, '): devolvendo à fila');
          await ack({ taskId: task.id, status: 'failed', error: `Operador não saiu da pausa (${stateRef.current ?? 'desconhecido'})`, retry: true });
          return;
        }
      }
      try {
        makeManualCallRef.current(task.phone);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.error('[ClickToCall] makeManualCall falhou:', message);
        await ack({ taskId: task.id, status: 'failed', error: message, retry: false });
        return;
      }
      const outcome = await waitForCallStart();
      switch (outcome) {
        case 'started':
          await ack({ taskId: task.id, status: 'calling' });
          return;
        case 'manual_failed':
          console.error('[ClickToCall] o SDK não conseguiu completar a chamada manual');
          await ack({ taskId: task.id, status: 'failed', error: 'Chamada manual falhou no SDK', retry: false });
          return;
        case 'interrupted':
          console.warn('[ClickToCall] chamada manual não aconteceu (estado', stateRef.current, '): devolvendo à fila');
          await ack({ taskId: task.id, status: 'failed', error: `Discador interrompido (${stateRef.current ?? 'desconhecido'})`, retry: true });
          return;
        case 'disposed':
          // Sem como saber se discou: devolve à fila para outra aba/sessão (keepalive)
          await ack({ taskId: task.id, status: 'failed', error: 'Discador encerrado durante a discagem', retry: true });
          return;
        default: {
          const message = `SDK não iniciou a chamada (estado continua ${stateRef.current ?? 'desconhecido'})`;
          console.error('[ClickToCall]', message);
          await ack({ taskId: task.id, status: 'failed', error: message, retry: false });
        }
      }
    };

    const tick = async () => {
      if (disposed || inFlight) {
        return;
      }
      inFlight = true;
      try {
        const state = stateRef.current;
        const claim = canDispatchTo(presenceFromOperatorState(state)) && Date.now() >= cooldownUntil;
        let result: PollResult;
        try {
          result = await pollClickToCall({ ...config, state, claim });
        } catch (error) {
          result = { ok: false, status: 0, code: error instanceof Error && error.name === 'AbortError' ? 'TIMEOUT' : 'NETWORK' };
        }
        if (disposed) {
          if (result.ok && result.task) {
            // A tarefa já saiu da fila no servidor: devolve para outra aba/sessão
            await ack({ taskId: result.task.id, status: 'failed', error: 'Discador encerrado antes de discar', retry: true });
          }
          return;
        }
        if (!result.ok) {
          failureStreak += 1;
          const notConfigured = result.code !== null && NOT_CONFIGURED_CODES.has(result.code);
          mode = notConfigured ? 'notConfigured' : 'backoff';
          if (notConfigured) {
            if (!notConfiguredWarned) {
              notConfiguredWarned = true;
              console.warn('[ClickToCall] backend sem click-to-call configurado (', result.code, '); nova tentativa em 5 min');
            }
          } else if (failureStreak === 1) {
            console.warn('[ClickToCall] polling falhou (HTTP', result.status, result.code ?? '', '); aplicando backoff');
          }
          applyInterval();
          return;
        }
        if (mode !== 'normal') {
          mode = 'normal';
          failureStreak = 0;
        }
        presenceTtlMs = result.presenceTtlSeconds * 1000;
        if (pollIntervalMs === undefined && result.pollIntervalMs > 0) {
          baseInterval = Math.max(1000, result.pollIntervalMs);
        }
        applyInterval();
        if (result.task) {
          try {
            await dispatch(result.task);
          } catch (error) {
            console.error('[ClickToCall] erro ao tratar a tarefa', result.task.id, ':', error instanceof Error ? error.message : error);
          }
        }
      } finally {
        inFlight = false;
      }
    };

    const ticker = createBackgroundTicker(() => void tick(), computeInterval());
    tickerRef.current = ticker;
    void tick();

    return () => {
      disposed = true;
      ticker.stop();
      if (tickerRef.current === ticker) {
        tickerRef.current = null;
      }
      if (applyIntervalRef.current === applyInterval) {
        applyIntervalRef.current = null;
      }
      reportClickToCallOffline(config);
    };
  }, [enabled, pollIntervalMs, sessionToken]);

  return null;
}
